import type { ParsedUrlQuery } from 'querystring';

/**
 * 폴더 재생 URL 계약 — 쓰기와 읽기를 한 곳에서 소유한다.
 *
 * 서버는 재생 커서를 들고 있지 않아 "지금 폴더를 재생 중"이라는 사실은 URL 로만 전달된다.
 * 쿼리 키 문자열이 이 파일 밖으로 새지 않게 한다.
 */
const FOLDER_ID_QUERY_KEY = 'folderId';

const FOLDER_ID_PATTERN = /^[0-9a-f]{24}$/i;

export interface FolderPlayContext {
  /** 재생 중인 폴더. 없으면 폴더 재생이 아니다. */
  folderId: string | undefined;
  /** 잘못된 ID도 폴더 재생으로 취급한다. */
  isMalformedFolderId: boolean;
}

/**
 * 지금 URL 이 폴더 재생인지 읽는다.
 *
 * `buildFolderPlayHref` 가 쓴 것을 그대로 되돌린다 — 둘은 짝이므로 한쪽만 고치지 말 것.
 */
export const readFolderPlayContext = (
  query: ParsedUrlQuery,
): FolderPlayContext => {
  const rawFolderId = query[FOLDER_ID_QUERY_KEY]?.toString();
  // 공백만 있는 값도 폴더 링크로 남겨 개인 다음 퀴즈로 넘어가지 않게 한다.
  const folderId = rawFolderId?.trim() || rawFolderId || undefined;
  return {
    folderId,
    isMalformedFolderId: !!folderId && !FOLDER_ID_PATTERN.test(folderId),
  };
};

/**
 * 공유용 절대 URL 에 폴더 재생 쿼리를 붙인다. 폴더 재생이 아니면 그대로 돌려준다.
 *
 * 받은 사람도 같은 폴더 순서로 이어 풀게 하려는 것이다. 공개 여부는 보지 않는다 —
 * 다음 퀴즈 판정은 서버가 받은 사람 기준으로 다시 하므로, 갈 수 없는 퀴즈는 거기서 걸러진다.
 */
export const withFolderPlayQuery = (
  url: string,
  folderId: string | undefined,
) => {
  if (!folderId) return url;
  const query = `${FOLDER_ID_QUERY_KEY}=${encodeURIComponent(folderId)}`;
  return `${url}${url.includes('?') ? '&' : '?'}${query}`;
};

export const buildFolderPlayHref = ({
  folderId,
  entryMapHashId,
}: {
  folderId: string;
  entryMapHashId: string;
}) => ({
  pathname: `/play/${entryMapHashId}`,
  query: { [FOLDER_ID_QUERY_KEY]: folderId },
});
